// Pure UI-model -> scenario-DSL mapping for the visual battle-script builder.
// No pixi. Mirrors the token grammar in src/dev/scenario.ts so the builder and
// the engine stay in lockstep. The builder edits ScriptCell objects; this turns
// them into DSL tokens that buildScenario() consumes.
//
// Skill index map is SparkyBot-specific (both demo fighters are SparkyBot):
// attack-skills are cannon=S1, earthshake=S2; the only buff is charge (P1=bare P);
// the only repair is repair (R); the only block skill is bunker (defender's B).

export type ScriptAction = 'punch' | 'kick' | 'cannon' | 'earthshake' | 'repair';

export type ScriptOutcome =
  | 'hit'
  | 'dodge'
  | 'dodgeCounter'
  | 'dodgeBuffCounter'
  | 'block'
  | 'pierce';

// One attacker turn in the builder. `buff` charges Charge before the swing;
// `counterKick` makes the defender's counter a kick. Both are ignored when not
// applicable (see normalizeCell).
export type ScriptCell = {
  action: ScriptAction;
  outcome: ScriptOutcome;
  buff: boolean;
  counterKick: boolean;
};

export const DEFAULT_CELL: ScriptCell = {
  action: 'punch',
  outcome: 'hit',
  buff: false,
  counterKick: false,
};

// Display order + labels for the palette. Icon textures are mapped by `action`
// in the render layer (kept out of this pure module).
export const ACTION_LIST: { action: ScriptAction; label: string }[] = [
  { action: 'punch', label: 'Punch' },
  { action: 'kick', label: 'Kick' },
  { action: 'cannon', label: 'Cannon' },
  { action: 'earthshake', label: 'Quake' },
  { action: 'repair', label: 'Repair' },
];

// Short badge labels for outcomes (colors live in the render layer).
export const OUTCOME_LIST: { outcome: ScriptOutcome; label: string }[] = [
  { outcome: 'hit', label: 'HIT' },
  { outcome: 'dodge', label: 'DODGE' },
  { outcome: 'dodgeCounter', label: 'CTR' },
  { outcome: 'dodgeBuffCounter', label: 'B+CTR' },
  { outcome: 'block', label: 'BLOCK' },
  { outcome: 'pierce', label: 'PIERCE' },
];

const SKILL_ACTIONS: ReadonlySet<ScriptAction> = new Set(['cannon', 'earthshake']);

export function isSkillAction(action: ScriptAction): boolean {
  return SKILL_ACTIONS.has(action);
}

// Repair consumes the whole turn (self-heal) - it has no defender outcome.
export function hasOutcome(action: ScriptAction): boolean {
  return action !== 'repair';
}

export function canBuff(action: ScriptAction): boolean {
  return action !== 'repair';
}

// Pierce only makes sense for a skill that could otherwise be blocked.
export function isOutcomeValid(action: ScriptAction, outcome: ScriptOutcome): boolean {
  if (!hasOutcome(action)) return outcome === 'hit';
  if (outcome === 'pierce') return isSkillAction(action);
  return true;
}

export function canCounterKick(outcome: ScriptOutcome): boolean {
  return outcome === 'dodgeCounter' || outcome === 'dodgeBuffCounter';
}

// Clamps a cell to a self-consistent state after an edit (e.g. switching the
// action to repair clears buff/outcome; pierce is dropped on non-skills).
export function normalizeCell(cell: ScriptCell): ScriptCell {
  if (cell.action === 'repair') {
    return { action: 'repair', outcome: 'hit', buff: false, counterKick: false };
  }
  const outcome = isOutcomeValid(cell.action, cell.outcome) ? cell.outcome : 'hit';
  return {
    action: cell.action,
    outcome,
    buff: cell.buff,
    counterKick: cell.counterKick && canCounterKick(outcome),
  };
}

const VALID_ACTIONS: ReadonlySet<string> = new Set(ACTION_LIST.map((a) => a.action));
const VALID_OUTCOMES: ReadonlySet<string> = new Set(OUTCOME_LIST.map((o) => o.outcome));

// Coerces untrusted data (e.g. JSON parsed from localStorage) into a valid cell.
// Unknown or missing fields fall back to defaults; the result is normalized.
export function coerceCell(raw: unknown): ScriptCell {
  const r = (raw ?? {}) as Record<string, unknown>;
  const action =
    typeof r.action === 'string' && VALID_ACTIONS.has(r.action)
      ? (r.action as ScriptAction)
      : DEFAULT_CELL.action;
  const outcome =
    typeof r.outcome === 'string' && VALID_OUTCOMES.has(r.outcome)
      ? (r.outcome as ScriptOutcome)
      : DEFAULT_CELL.outcome;
  return normalizeCell({ action, outcome, buff: !!r.buff, counterKick: !!r.counterKick });
}

const OUTCOME_SUFFIX: Record<ScriptOutcome, string> = {
  hit: '',
  dodge: 'D',
  dodgeCounter: 'DC',
  dodgeBuffCounter: 'DPC',
  block: 'B',
  pierce: 'P',
};

const ACTION_CORE: Record<Exclude<ScriptAction, 'repair'>, string> = {
  punch: 'A',
  kick: 'AK',
  cannon: 'AS1',
  earthshake: 'AS2',
};

// One cell -> one DSL token. Assembles in grammar order: [P]A[K][S<n>][outcome][K].
export function cellToToken(cell: ScriptCell): string {
  const c = normalizeCell(cell);
  if (c.action === 'repair') return 'R';
  const buff = c.buff ? 'P' : '';
  const suffix = OUTCOME_SUFFIX[c.outcome];
  const counter = c.counterKick && canCounterKick(c.outcome) ? 'K' : '';
  return `${buff}${ACTION_CORE[c.action]}${suffix}${counter}`;
}

// One fighter's cells -> a whitespace-joined scenario spec string.
export function cellsToSpec(cells: ScriptCell[]): string {
  return cells.map(cellToToken).join(' ');
}
